import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import FirstFold from '@components/course-landing-page/first-fold'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { formatSeoConfig } from '@lib/seo'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import CodingalProductBlog from '@components/blog/product-blogs'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import ContentCard from '@components/course-landing-page/content-card'
import CourseDescription from '@components/course-landing-page/course-description'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

export const BootstrapSeo = formatSeoConfig({
  title: 'Bootstrap for Kids: Web Design Online Classes',
  description:
    'Bootstrap for kids taught by expert instructors. Build responsive, professional websites with minimal code and a structured curriculum. Try a free lesson.',
  url: 'https://www.codingal.com/courses/bootstrap-web-design-for-kids/',
  metaImage:
    'https://fastly.codingal.com/images/courses/og/codingal-light-v1.jpg',
})

const BegineerGuide = () => (
  <>
    <h3 className="font-600 text-lg lg:text-xl">
      Why Should Kids Learn Bootstrap?
    </h3>
    <p>Learning Bootstrap is a great way for kids to:</p>
    <ul>
      <li>
        <strong>Boost creativity</strong> by designing and coding their own
        responsive websites and projects.
      </li>
      <li>
        <strong>Build problem-solving skills</strong> through organizing
        layouts, styling elements, and fixing design challenges.
      </li>
      <li>
        <strong>Understand the basics of web development</strong>, a stepping
        stone to future careers in technology, design, and programming.
      </li>
      <li>
        <strong>Express themselves</strong> by turning their ideas into visually
        stunning and interactive web pages.
      </li>
      <li>
        <strong>Gain a strong foundation</strong> for learning advanced coding
        languages like JavaScript, React, and CSS frameworks.
      </li>
    </ul>
    <p>
      Bootstrap helps kids see coding as creative and fun, giving them the tools
      to design professional-looking websites while building confidence and
      real-world digital skills.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      How Do You Explain Bootstrap to a Child?
    </h3>
    <p>
      Bootstrap is like a box of colorful building blocks for websites. Instead
      of starting from scratch, kids can use ready-made pieces, like buttons,
      menus, and grids, to design their own web pages.
    </p>
    <p>
      It’s just like digital LEGO, but instead of building castles or cars, kids
      build websites that look amazing on any device—computers, tablets, or
      phones.
    </p>
    <p>
      With Bootstrap, kids learn how to make their pages look neat, stylish, and
      interactive, turning their creative ideas into real, working websites!
    </p>
    <h2>An Easy Way to Understand Bootstrap for Kids</h2>
    <p>
      Here’s a simple analogy to help you understand Bootstrap. Imagine you’re
      decorating a room for a big event. First, you set up the walls and
      floor—that’s like your website’s structure. Then, you add furniture,
      lights, and decorations—those are the buttons, menus, and colors. Finally,
      you make sure everything looks neat and fits perfectly—that’s Bootstrap in
      action!
    </p>
    <p>
      Bootstrap gives you all the ready-made tools you need to design beautiful,
      responsive websites quickly. Instead of building everything from scratch,
      kids can use Bootstrap’s pre-designed blocks to make their web pages look
      professional and stylish.
    </p>
    <p>
      This is exactly how kids learn Bootstrap in our fun, beginner-friendly
      Bootstrap classes for kids—taking their creative ideas, adding structure,
      and turning them into stunning interactive websites. From colorful
      portfolios to fun digital projects, Bootstrap makes their imagination come
      alive on the web!
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      What Kids Can Learn with Bootstrap?
    </h3>
    <p>
      When kids learn Bootstrap, they’re not just typing code—they’re designing
      real websites! Bootstrap teaches children how to think like web designers
      and problem solvers. It gives them the skills to plan, organize, and bring
      their creative ideas to life while having fun with coding and design.
    </p>
    <ul>
      <li>
        <strong>Web Design Basics</strong> – How to structure pages, organize
        content, and create layouts that look clean and professional.
      </li>
      <li>
        <strong>Styling &amp; Creativity</strong> – Using colors, fonts, and
        themes to design websites that reflect their personality and ideas.
      </li>
      <li>
        <strong>Coding Logic</strong> – Learning how HTML, CSS, and Bootstrap
        work together to make web pages dynamic and responsive.
      </li>
      <li>
        <strong>Problem-Solving Skills</strong> – Fixing layout issues, testing
        their designs, and improving their websites through feedback.
      </li>
      <li>
        <strong>Interactive Projects</strong> – Building real, shareable
        websites like portfolios, digital storybooks, or mini web apps.
      </li>
    </ul>
    <p>
      ✨ <strong>In short</strong>, Bootstrap turns kids into digital creators,
      helping them combine coding, creativity, and design thinking to build
      something uniquely their own — one webpage at a time! 🚀
    </p>
  </>
)

const { BOOTSTRAP } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['wd'].trialCourseQuery}`

const Bootstrap = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...BootstrapSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[BOOTSTRAP].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName="Learn Bootstrap with Top AI & Coding Course Instructors for Kids and Teens"
        description="Empower your child to move from consuming content to creating it. Codingal’s website development course teaches Bootstrap to kids and teens the building blocks of the web, enabling them to design and structure their own websites. With guidance from expert instructors, students will embark on a journey that combines creativity with foundational coding skills, setting the stage for future exploration in AI & coding."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={BOOTSTRAP}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={BOOTSTRAP} />
          <CourseFeatures
            courseTitle={BOOTSTRAP}
            title={
              <Trans>
                An engaging course for kids to build responsive website pages
              </Trans>
            }
            description={
              <>
                <p>
                  Learning to build websites gives your child valuable digital
                  skills. In our website development course, kids will learn how
                  to adapt web pages that adjust seamlessly across all
                  devices—whether it’s a laptop, tablet, or smartphone—using
                  Bootstrap, a widely used framework by professional developers.
                </p>
                <p>
                  Each lesson is filled with hands-on activities where students
                  learn to design, organize, and style content using easy-to-use
                  building blocks. By the end of the course, your child will be
                  able to build responsive web pages that look great and
                  function well on any screen, giving them the skills to bring
                  their creative ideas to life online.
                </p>
              </>
            }
          />
          <ClassCurriculum
            courseName={BOOTSTRAP}
            title="Our curriculum"
            description={
              <Trans>
                The Bootstrap curriculum is designed to take students from
                foundational concepts to practical web design and development
                skills. Beginning with setting up development tools and creating
                simple web pages, students gradually learn to use containers,
                typography, utilities, carousels, and other interactive
                Bootstrap components. Each lesson builds on the previous one,
                enabling students to design structured, responsive, and visually
                appealing websites. It is like building a digital LEGO website,
                where every block comes together to create something amazing!
              </Trans>
            }
          />
          <Activities courseName={BOOTSTRAP} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked AI & coding
                tutors ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                Our instructors bring deep expertise in Computer Science and
                complete rigorous training before they teach. They’re truly
                amazing, and it shows in our exceptional Net Promoter Score
                (NPS) of 86! Students and parents consistently rate our teachers
                4.9 out of 5, and our curriculum earns a fantastic 4.8 out of 5.
                We’re so proud to have a faculty that’s 91% female and 9% male,
                creating an inclusive, encouraging learning environment where
                everyone feels welcome. Each tutor is trained to understand
                individual student needs and deliver personalized support, even
                in group settings. Plus, students have 24/7 access to expert
                tutors for instant doubt resolution, anytime they need help.
                This makes learning Bootstrap a truly supported experience,
                especially through our virtual Bootstrap lessons that connect
                kids with top educators worldwide.
              </Trans>
            }
          />
          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals of
                Game Development with Bootstrap
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  Codingal offers a nurturing environment where middle and high
                  school learners can explore the world of game development with
                  Bootstrap without fear. Our structured curriculum, combined
                  with expert instructors, ensures that students build a strong
                  foundation in coding, preparing them for exciting future
                  endeavors in AI, game design, and programming.
                </span>
              </Trans>
            }
            courseName={BOOTSTRAP}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                At Codingal, your child masters Bootstrap through a STEM.org -
                accredited program built on proven educational frameworks like
                Bloom’s Taxonomy, BIDE, and STEAM. Our curriculum is aligned
                with Common Core, state standards, AP Computer Science goals,
                and the K - 12 Computer Science Framework - ensuring learning
                that’s both age-appropriate and college-ready. Through hands-on,
                project-based learning, students create real-world apps like
                temperature converters and scientific calculators, applying
                math, science, and engineering logic to solve everyday problems.
                Backed by STEM.org’s rigorous audits, every Bootstrap lesson
                lays a strong, future-proof foundation in STEM.
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your Bootstrap certifications"
            description={
              <>
                In the Website Development track, students in grades 6 - 12
                begin their journey with Bootstrap, the foundational language of
                the web. They learn how to structure web pages using elements
                like headings, paragraphs, links, images, lists, tables, and
                forms. As they progress, students apply semantic Bootstrap to
                improve accessibility and build pages that mirror real-world
                websites. Through hands-on lessons and creative projects, they
                transform code into fully functional web pages. By the end of
                the Bootstrap module, students not only understand how websites
                are built but also develop a strong foundation for diving into
                web design, interactivity with JavaScript, and more advanced web
                technologies.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our Bootstrap classes are appropriate for middle school & high school students"
            description={
              <>
                At Codingal, our Bootstrap classes are designed especially for
                middle and high school students who are ready to move from
                simple coding to real game development.
                <br />
                Our foundational Bootstrap curriculum introduces students to
                Python game design in a fun, beginner-friendly way. They start
                with the basics, creating game windows, drawing shapes, and
                moving characters. As they progress, students learn how to add
                sprites, control movement with keyboard inputs, use sounds, and
                build levels with scoring and timers.
                <br />
                Step by step, they advance into more complex concepts like
                collision detection, animations, and designing full interactive
                games. With each project, students gain practical experience in
                game design, coding logic, and problem-solving , while creating
                their very own playable games.
                <br />
                By the end of the course, kids not only strengthen their Python
                skills but also gain the confidence to design and share games,
                preparing them for more advanced coding journeys and
                future-ready STEM skills.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses
            description={
              <>
                Once your child has explored the basics of website design with
                Bootstrap, there’s a whole world of coding and creativity
                waiting to be unlocked! We recommend pairing this course with
                other beginner-friendly and exciting programs to keep the
                momentum going.
              </>
            }
            courseCodes={['wd', 'cgm']}
          />
          <ContentCard
            urlQuery={urlQuery}
            title={<>The Ultimate Guide to Bootstrap for Kids</>}
            description={<BegineerGuide />}
          />
          <Faq
            courseName={BOOTSTRAP}
            title="Frequently Asked Questions: Bootstrap for Kids"
          />
          <CodingalProductBlog urlQuery={urlQuery} isCoursePage />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={BOOTSTRAP}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Bootstrap for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default Bootstrap
